import "./style.scss";

/* P4-01c: option chips for an agent's clarifying question (ask_user
 * suspend/resume, design §5). Rendered under the question bubble on the
 * LATEST turn only; a click sends the option's label as the user's reply —
 * the agent reads the reply as prose (D12), so the label is the message.
 * Free text in the normal input always remains valid, which is why this is
 * a group of suggestions, not a form control. House pill recipe per
 * DESIGN_GUIDE §3; real <button>s in a labeled group for AT users. */

export interface AskUserOption {
    id?: string;
    label: string;
    description?: string;
}

export interface AskUserPrompt {
    question: string;
    options?: AskUserOption[];
    allowFreeText?: boolean;
}

export const AskUserOptions = ({
    options,
    onSelect,
    onOther
}: {
    options: AskUserOption[];
    onSelect: (label: string) => void;
    /**
     * The free-text escape hatch: the offered options may not be exhaustive
     * (the agent offers what its evidence surfaced), and typing in the chat
     * input always works — but that isn't discoverable on its own (Ken,
     * 2026-09-09). The trailing "Something else…" chip makes it visible;
     * clicking it hands focus to the chat input.
     */
    onOther?: () => void;
}) => {
    if (!options.length) return null;
    return (
        <div className="ask-user-options" role="group" aria-label="Answer choices">
            {options.map((option, index) => (
                <button
                    key={option.id ?? `${index}-${option.label}`}
                    type="button"
                    className="ask-user-options__chip"
                    onClick={() => onSelect(option.label)}
                >
                    <span className="ask-user-options__label">{option.label}</span>
                    {option.description && (
                        <span className="ask-user-options__description">{option.description}</span>
                    )}
                </button>
            ))}
            {onOther && (
                <button
                    type="button"
                    className="ask-user-options__chip ask-user-options__chip--other"
                    onClick={onOther}
                >
                    <span className="ask-user-options__label">Something else…</span>
                    <span className="ask-user-options__description">
                        Type your answer in the message box below
                    </span>
                </button>
            )}
        </div>
    );
};
